import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Grid, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import API from './services/api';

const WCB_INCIDENT_TITLES = new Set([
  'fatalities',
  'near misses',
  'injuries lost-time',
  'injuries non-lost time',
  'injuries non-lost time',
  'hospital admittance'
]);

const normalizeIncidentList = (payload) => {
  if (Array.isArray(payload?.incidents)) return payload.incidents;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const isAffirmative = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return ['yes', 'true', '1', 'y'].includes(normalized);
};

const isWcbIncident = (incident = {}) => {
  const normalizedTitle = String(incident.TITLE || incident.title || '').trim().toLowerCase();
  return WCB_INCIDENT_TITLES.has(normalizedTitle)
    || isAffirmative(incident.FORWARD_TO_WCB)
    || isAffirmative(incident.forwardToWCB)
    || isAffirmative(incident.forwardToWcb)
    || isAffirmative(incident.WCB_REPORT_REQUIRED)
    || isAffirmative(incident.wcbReportRequired);
};

const sortByNewestDate = (left, right) => {
  const leftTime = new Date(left?.DATE || left?.date || 0).getTime();
  const rightTime = new Date(right?.DATE || right?.date || 0).getTime();
  return rightTime - leftTime;
};

const WCBClerkIncidentList = () => {
  const [incidents, setIncidents] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchIncidents = async () => {
      setError('');

      const loadFromWcbEndpoint = async () => {
        const response = await API.get('/api/incidents/wcb/list');
        const nextIncidents = normalizeIncidentList(response.data);

        if (!Array.isArray(nextIncidents)) {
          throw new Error('Unexpected WCB incident response format');
        }

        return nextIncidents.sort(sortByNewestDate);
      };

      const loadFromGeneralIncidents = async () => {
        const response = await API.get('/api/incidents');
        const allIncidents = normalizeIncidentList(response.data);

        if (!Array.isArray(allIncidents)) {
          throw new Error('Unexpected incident list response format');
        }

        return allIncidents.filter(isWcbIncident).sort(sortByNewestDate);
      };

      try {
        const nextIncidents = await loadFromWcbEndpoint();
        setIncidents(nextIncidents);
      } catch (wcbError) {
        console.warn('WCB list endpoint failed, falling back to general incidents list:', wcbError);

        try {
          const fallbackIncidents = await loadFromGeneralIncidents();
          setIncidents(fallbackIncidents);
        } catch (fallbackError) {
          console.error('Failed to fetch WCB incidents from both endpoints:', fallbackError);
          setError(
            fallbackError.response?.data?.message ||
            fallbackError.response?.data?.error ||
            wcbError.response?.data?.message ||
            wcbError.response?.data?.error ||
            fallbackError.message ||
            wcbError.message ||
            'Failed to fetch incidents.'
          );
        }
      }
    };

    fetchIncidents();
  }, []);

  if (error) return <div>{error}</div>;

  return (
    <Card sx={{ maxWidth: 900, margin: '0 auto', mt: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>WCB Clerk Incident Queue</Typography>
        <Grid container spacing={2}>
          {incidents.length === 0 && (
            <Grid item xs={12}>
              <Typography>No incidents requiring WCB review.</Typography>
            </Grid>
          )}
          {incidents.map((incident) => (
            <Grid item xs={12} key={incident.ID}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1">
                    {incident.TITLE} ({incident.STATUS}) - {incident.SEVERITY}
                  </Typography>
                  <Typography variant="body2">
                    Date: {incident.DATE}
                  </Typography>
                  <Typography variant="body2">
                    WCB Claim #: {incident.WCB_CLAIM_NUMBER || 'N/A'}
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ mt: 1 }}
                    onClick={() => navigate(`/wcb-review/${incident.ID}`)}
                  >
                    Review & Edit
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default WCBClerkIncidentList;